import * as React from 'react';

import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';

const CLEVER_APP_LINK = (
  <Link href={LinkConstants.CLEVER_APPLICATIONS} target="_blank">
    here
  </Link>
);

const CLEVER_SUPPORT_LINK = (
  <Link href={LinkConstants.CLEVER_SUPPORT} target="_blank">
    Clever support
  </Link>
);

const SHARING_SETTINGS_LINK = (
  <Link href={LinkConstants.CONNECT_SIS} target="_blank">
    here
  </Link>
);

export type NextStep = {
  intro: React.ReactNode;
  bullets?: React.ReactNode[];
  footer?: React.ReactNode;
};

export type SyncHoldInformation = {
  error: string;
  reason: string;
  nextStep: NextStep;
};

export type SyncHold =
  | 'CleverDisconnectedHold'
  | 'TooManyDestructiveChangesHold'
  | 'EmergencyContactHold'
  | 'TooManyContactPointsHold';
type SyncHoldCopyMap = { [K in SyncHold]: SyncHoldInformation | undefined };

export const CleverSyncHoldCopy: SyncHoldCopyMap = {
  CleverDisconnectedHold: {
    error: 'Clever is disconnected',
    reason: 'Your SIS sync has been paused because we are unable to communicate with Clever.',
    nextStep: {
      intro: 'Check your settings in Clever. Make sure of the following:',
      bullets: [
        <span>
          You have the Remind app installed in Clever. You can view it {CLEVER_APP_LINK}.
        </span>,
        <span>
          You are sharing the correct data with Clever. We recommending sharing district data. Learn
          about sharing settings with Remind {SHARING_SETTINGS_LINK}.
        </span>,
      ],
      footer: 'Once you’ve addressed the above, resume the sync below.',
    },
  },
  EmergencyContactHold: {
    error: 'Secondary contacts included',
    reason: 'Your SIS sync has been paused because we suspect a data configuration error in Clever.',
    nextStep: {
      intro:
        'Emergency and secondary contacts aren’t typically contacted by teachers so we do not recommend creating accounts for them. Download the CSV of the additional contacts and review them. If you wish to sync them, create accounts for them below. Otherwise, review your settings in Clever to ensure that emergency and secondary contacts are filtered out from sync, then return here to check for new Clever data.',
    },
  },
  TooManyDestructiveChangesHold: {
    error: 'Removed',
    reason: 'Your SIS sync has been paused because we detected a large number of removals.',
    nextStep: {
      intro:
        <span>If the removals are unexpected, contact your SIS Administrator or {CLEVER_SUPPORT_LINK} and undo the removals in your SIS or Clever, then check for new Clever data. If the removals are expected, confirm them below.</span>,
    },
  },
  TooManyContactPointsHold: {
    error: 'Too many parent connections',
    reason: 'Your SIS sync has been paused because we suspect a data configuration error in your SIS or in Clever.',
    nextStep: {
      intro:
        'Accounts in your school data have six or more points of contact associated with them. This could be a mistake in your configuration. Download the CSV of these accounts and review them. If you wish to sync them, resume the sync below. Otherwise, correct them in your SIS or Clever and return here to check for new Clever data.',
    },
  },
};

export const SftpSyncHoldCopy: SyncHoldCopyMap = {
  // These holds should never be hit for sftp formats
  CleverDisconnectedHold: undefined,
  EmergencyContactHold: undefined,
  TooManyDestructiveChangesHold: {
    error: 'Resources removed',
    reason: 'Your SIS sync has been paused because we detected a large number of removals.',
    nextStep: {
      intro:
        'If the removals are unexpected, click "How to fix my files" below and follow the steps in the help center article to update your CSV files and resend to Remind. If the removals are expected, confirm them below to proceed.',
    },
  },
  TooManyContactPointsHold: {
    error: 'Too many parent connections',
    reason: 'Your SIS sync has been paused because we suspect a data configuration error in your SIS.',
    nextStep: {
      intro:
        'Accounts in your school data have six or more parents associated with them. This could be a mistake in your configuration. Download the CSV of these accounts and review them. If you wish to sync them, create accounts for them below. Otherwise, update your CSV files to have the proper parent connections, send the new CSV files to Remind, and reload this page to resume the sync. ',
    },
  },
};
